import { useNavigate } from 'react-router-dom';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BulbOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertBanner } from '../../../AlertBanner';
import { SignInButton } from '../../../SignInButton';
import { UserContext } from '../../../UserContext';
import '../../../styles/global.css';
import { formatDateFromUTCtoAgent } from '../../agentSms/utils';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ConversationNote {
  id: string;
  content: string;
  created_at: string;
  user?: {
    summary?: string;
    email?: string;
  };
}

interface SupportTicket {
  id: string;
  incident_number: number;
  description?: string;
  status?: string;
  created_at?: string;
  resolved_at?: string;
  acknowledged_at?: string;
  assignee?: string;
  escalated_to?: string;
  resolution?: string;
  notes?: string;
  resolution_notes?: string;
  linked_message_id?: string;
  conversation_thread?: ConversationNote[];
  source_id?: string;
  client_id: number;
  agent_id: string;
  agent?: {
    first_name: string;
    last_name: string;
    email: string;
    timezone?: string;
  };
  intelligence?: IntelligenceData;
}

interface ActionStep {
  step_number: number;
  action: string;
  reasoning: string;
}

interface IntelligenceData {
  action_plan: {
    summary: string;
    action_steps: ActionStep[];
    confidence: string;
    confidence_explanation: string;
    patterns_observed: string[];
  };
  similar_incidents: Array<{
    incident_number: number;
    description: string;
    resolution_notes: string;
    resolution: string;
    assignee: string;
    score: number;
  }>;
}

const ConversationMessage = ({
  message,
  isAgent,
  timezone,
}: {
  message: { content: string; author: string; timestamp: string };
  isAgent: boolean;
  timezone?: string;
}): JSX.Element => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 10px',
        background: isAgent ? '#f0f7ff' : '#fafafa',
        borderRadius: '4px',
        borderLeft: `3px solid ${isAgent ? '#1890ff' : '#52c41a'}`,
        marginBottom: '6px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: '12px',
            color: isAgent ? '#1890ff' : '#52c41a',
          }}
        >
          {isAgent ? '👤' : '🛠️'} {message.author}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: '#999',
          }}
        >
          {formatDateFromUTCtoAgent(
            message.timestamp,
            timezone || 'America/New_York',
          )}
        </span>
      </div>
      <div
        style={{
          color: '#333',
          whiteSpace: 'pre-wrap',
          lineHeight: '1.5',
          fontSize: '13px',
        }}
      >
        {message.content}
      </div>
    </div>
  );
};

export default function SupportTicketManagementPage({
  apiUrl,
}: {
  apiUrl: string;
}): JSX.Element {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [engineers, setEngineers] = useState<string[]>([]);
  const [clients, setClients] = useState<Array<{ id: number; name: string }>>(
    [],
  );
  const [error, setError] = useState<string | undefined>();
  const [filters, setFilters] = useState({
    status: 'All',
    clientId: '',
    engineerName: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
  });
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const refreshInterval = useRef<NodeJS.Timeout | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [intelligenceData, setIntelligenceData] =
    useState<IntelligenceData | null>(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [form] = Form.useForm();

  const user = useContext(UserContext);

  const fetchTickets = useCallback(
    async (
      showLoading = true,
      page = pagination.current,
      pageSize = pagination.pageSize,
    ) => {
      if (!user?.accessToken) {
        return null;
      }

      if (showLoading) {
        setLoading(true);
      }
      setError(undefined);

      try {
        const queryParams = new URLSearchParams();
        if (filters.status !== 'All') {
          queryParams.append('status', filters.status);
        }
        if (filters.clientId) {
          // Use client_id parameter as expected by the API
          queryParams.append('client_id', filters.clientId);
        }
        if (filters.engineerName) {
          queryParams.append('engineer_name', filters.engineerName);
        }
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
          queryParams.append(
            'start_date',
            filters.dateRange[0].startOf('day').toISOString(),
          );
          queryParams.append(
            'end_date',
            filters.dateRange[1].endOf('day').toISOString(),
          );
        }

        queryParams.append('page', page.toString());
        queryParams.append('page_size', pageSize.toString());

        const response = await fetch(
          `${apiUrl}/api/pagerduty/incidents?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error('Failed to fetch tickets');
        }

        const data = await response.json();
        const results = data.incidents || [];
        setTickets(results);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
          current: data.page || 1,
          pageSize: data.page_size || 10,
        }));

        // Populate engineer and client lists if not already done
        if (results.length > 0) {
          const uniqueEngineers: string[] = Array.from(
            new Set(
              results
                .map((t: SupportTicket) => t.assignee)
                .filter(Boolean) as string[],
            ),
          ).sort();
          setEngineers(prev => {
            const combined = [...prev, ...uniqueEngineers].filter(
              (name): name is string => Boolean(name),
            );
            return Array.from(new Set(combined)).sort();
          });

          // Clients are now provided directly by the backend in the data.clients response
        }

        return results;
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load support tickets. Please try again later.');
        return null;
      } finally {
        if (showLoading) {
          setLoading(false);
        }
        setIsRefreshing(false);
      }
    },
    [
      apiUrl,
      user?.accessToken,
      filters,
      pagination.current,
      pagination.pageSize,
    ],
  );

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    const fetchFilters = async () => {
      if (!user?.accessToken) {
        return;
      }
      try {
        const response = await fetch(`${apiUrl}/api/pagerduty/filters`, {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.assignees) {
            setEngineers(data.assignees);
          }
          if (data.clients) {
            setClients(data.clients);
          }
        }
      } catch (err) {
        console.error('Error fetching filters:', err);
      }
    };

    fetchFilters();
  }, [apiUrl, user?.accessToken]);

  // Update selectedTicket when tickets list updates (for background refresh)
  useEffect(() => {
    if (selectedTicket && tickets.length > 0) {
      const updated = tickets.find(
        t => t.incident_number === selectedTicket.incident_number,
      );
      if (
        updated &&
        JSON.stringify(updated) !== JSON.stringify(selectedTicket)
      ) {
        setSelectedTicket(updated);
      }
    }
  }, [tickets, selectedTicket]);

  useEffect(() => {
    if (!isModalVisible || !selectedTicket || !user?.accessToken) {
      return;
    }

    // Use intelligence already on the ticket if available
    if (selectedTicket.intelligence) {
      setIntelligenceData(selectedTicket.intelligence);
      setIntelligenceLoading(false);
      return;
    }

    // Otherwise generate it via API (will persist to DB for future loads)
    setIntelligenceLoading(true);
    setIntelligenceData(null);
    fetch(
      `${apiUrl}/api/intelligence/incidents/${selectedTicket.incident_number}`,
      {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      },
    )
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        setIntelligenceData(data);
        // Refresh tickets so the persisted intelligence is available next time
        if (data) {
          fetchTickets(false);
        }
      })
      .catch(() => setIntelligenceData(null))
      .finally(() => setIntelligenceLoading(false));
  }, [isModalVisible, selectedTicket?.incident_number, user?.accessToken]);

  const handleRefresh = () => {
    setIsRefreshing(false);
    fetchTickets();
  };

  const showTicketDetails = (ticket: SupportTicket) => {
    setIntelligenceData(null);
    setSelectedTicket(ticket);
    setIsModalVisible(true);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Resolved':
        return 'success';
      case 'Acknowledged':
        return 'processing';
      case 'Triggered':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'incident_number',
      key: 'incident_number',
      render: (id: number, record: SupportTicket) => (
        <Button
          onClick={() =>
            window.open(
              `https://housewhisper.pagerduty.com/incidents/${record.source_id}`,
              '_blank',
            )
          }
          style={{ padding: 0, textDecoration: 'underline' }}
          type="link"
        >
          #{id}
        </Button>
      ),
      sorter: (a: SupportTicket, b: SupportTicket) =>
        a.incident_number - b.incident_number,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ fontWeight: 600 }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Client ID',
      dataIndex: 'client',
      key: 'client',
      render: (text: string, record: SupportTicket) => (
        <div
          style={{
            maxWidth: 250,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {record.client_id}
        </div>
      ),
    },
    {
      title: 'Agent',
      key: 'agent',
      ellipsis: true,
      render: (_: any, record: SupportTicket) => (
        <Button
          onClick={() =>
            window.open(
              `/agentsms/${record.client_id}?agentId=${record.agent_id}`,
              '_blank',
            )
          }
          style={{ padding: 0 }}
          type="link"
        >
          {record.agent
            ? `${record.agent.first_name} ${record.agent.last_name}`
            : 'Unknown'}
        </Button>
      ),
    },
    {
      title: 'Assignee',
      dataIndex: 'assignee',
      key: 'assignee',
      render: (assignee: string) => assignee || 'Unassigned',
    },
    {
      title: 'Subject/Title',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <div
            style={{
              maxWidth: 250,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string, record: SupportTicket) =>
        formatDateFromUTCtoAgent(
          date,
          record.agent?.timezone || 'America/New_York',
        ),
      sorter: (a: SupportTicket, b: SupportTicket) =>
        dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: SupportTicket) => (
        <Space>
          {record.status?.toLowerCase() === 'triggered' && (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() =>
                handleAcknowledge(record.incident_number, record.source_id)
              }
              size="small"
              type="primary"
            >
              Acknowledge
            </Button>
          )}
          <Button
            icon={<EyeOutlined />}
            onClick={() => showTicketDetails(record)}
            size="small"
          >
            Details
          </Button>
        </Space>
      ),
    },
  ];

  const handleResolve = async (incidentNumber: number, sourceId?: string) => {
    if (!user?.accessToken) {
      return;
    }
    const identifier = sourceId || incidentNumber.toString();
    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${identifier}/resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            email: user.name || 'admin@housewhisper.ai',
            resolution_note: 'Resolved via Support Dashboard',
          }),
        },
      );

      if (response.ok) {
        message.success(`Incident #${incidentNumber} resolved`);

        // Optimistic update
        const updatedTicket: SupportTicket = {
          ...tickets.find(t => t.incident_number === incidentNumber)!,
          status: 'Resolved',
          resolved_at: new Date().toISOString(),
        };

        // Update list state
        setTickets(prev =>
          prev.map(t =>
            t.incident_number === incidentNumber ? updatedTicket : t,
          ),
        );

        // Update selected ticket if it's the same one (though modal closes)
        if (selectedTicket?.incident_number === incidentNumber) {
          setSelectedTicket(updatedTicket);
        }

        // Background fetch to sync
        fetchTickets(false);
        setIsModalVisible(false);
      } else {
        throw new Error('Failed to resolve incident');
      }
    } catch (err) {
      message.error('Failed to resolve incident');
    }
  };

  const handleAcknowledge = async (
    incidentNumber: number,
    sourceId?: string,
  ) => {
    if (!user?.accessToken) {
      return;
    }
    const identifier = sourceId || incidentNumber.toString();
    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${identifier}/acknowledge`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            email: user.name || 'admin@housewhisper.ai',
          }),
        },
      );

      if (response.ok) {
        message.success(`Incident #${incidentNumber} acknowledged`);

        // Optimistic update
        const updatedTicket: SupportTicket = {
          ...tickets.find(t => t.incident_number === incidentNumber)!,
          status: 'Acknowledged',
          acknowledged_at: new Date().toISOString(),
        };

        // Update list state
        setTickets(prev =>
          prev.map(t =>
            t.incident_number === incidentNumber ? updatedTicket : t,
          ),
        );

        // Update selected ticket if it's the same one
        if (selectedTicket?.incident_number === incidentNumber) {
          setSelectedTicket(updatedTicket);
        }

        // Background fetch to sync
        fetchTickets(false);
      } else {
        throw new Error('Failed to acknowledge incident');
      }
    } catch (err) {
      message.error('Failed to acknowledge incident');
    }
  };

  const handleReopen = async (incidentNumber: number) => {
    if (!user?.accessToken) {
      return;
    }
    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${incidentNumber}/reopen`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            email: user.name || 'admin@housewhisper.ai',
            reopen_note: 'Reopened via Support Dashboard',
          }),
        },
      );

      if (response.ok) {
        message.success(`Incident #${incidentNumber} reopened`);

        // Optimistic update
        const updatedTicket: SupportTicket = {
          ...tickets.find(t => t.incident_number === incidentNumber)!,
          status: 'Triggered',
          resolved_at: undefined,
        };

        // Update list state
        setTickets(prev =>
          prev.map(t =>
            t.incident_number === incidentNumber ? updatedTicket : t,
          ),
        );

        if (selectedTicket?.incident_number === incidentNumber) {
          setSelectedTicket(updatedTicket);
        }

        // Background fetch to sync
        fetchTickets(false);
        setIsModalVisible(false);
      } else {
        throw new Error('Failed to reopen incident');
      }
    } catch (err) {
      message.error('Failed to reopen incident');
    }
  };

  const handleAddNote = async () => {
    if (!user?.accessToken || !selectedTicket || !replyContent.trim()) {
      return;
    }
    const identifier =
      selectedTicket.source_id || selectedTicket.incident_number.toString();
    setIsSubmittingReply(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${identifier}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            email: user.name || 'admin@housewhisper.ai',
            note: replyContent,
          }),
        },
      );

      if (response.ok) {
        message.success('Note added successfully');
        setReplyContent('');

        // Parse response to get the new note
        const data = await response.json();
        const newNote = data.note; // Should verify structure

        if (newNote && selectedTicket) {
          // Create optimistic updated ticket
          const updatedConversation = [
            ...(selectedTicket.conversation_thread || []),
            {
              id: newNote.id || `temp-${Date.now()}`,
              content: newNote.content,
              created_at: newNote.created_at || new Date().toISOString(),
              user: {
                summary: newNote.user?.summary || user.name || 'Admin',
                email: newNote.user?.email || user.name,
              },
            },
          ];

          const updatedTicket: SupportTicket = {
            ...selectedTicket,
            conversation_thread: updatedConversation,
          };

          // Update selected ticket immediately
          setSelectedTicket(updatedTicket);

          // Update in tickets list too
          setTickets(prev =>
            prev.map(t =>
              t.incident_number === selectedTicket.incident_number
                ? updatedTicket
                : t,
            ),
          );
        }

        // Background fetch to sync
        fetchTickets(false);
      } else {
        throw new Error('Failed to add note');
      }
    } catch (err) {
      message.error('Failed to add note');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!user?.accessToken || !selectedTicket) {
      return;
    }
    message.info(
      'Assignment override implementation would go here (PagerDuty API call required)',
    );
    // In a real scenario, this would call a PUT /api/pagerduty/incidents/{id} with new assignee
  };

  return (
    <div style={{ padding: '24px' }}>
      <AuthenticatedTemplate>
        <AlertBanner error={error} setError={setError} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <Typography.Title level={2}>
            Support Ticket Management
          </Typography.Title>
          <Button
            icon={<ReloadOutlined spin={isRefreshing} />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </div>

        <Card style={{ marginBottom: '24px' }}>
          <Form
            form={form}
            initialValues={filters}
            layout="inline"
            onValuesChange={(_, allValues) => {
              // Ensure clientId is properly handled for the API
              const filters = {
                ...allValues,
                clientId:
                  allValues.clientId === '' ? undefined : allValues.clientId,
              };
              setFilters(filters);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
          >
            <Form.Item label="Status" name="status">
              <Select style={{ width: 150 }}>
                <Option value="All">All Statuses</Option>
                <Option value="Triggered">New (Triggered)</Option>
                <Option value="Acknowledged">Open (Acknowledged)</Option>
                <Option value="Resolved">Resolved</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Client" name="clientId">
              <Select
                allowClear
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                optionFilterProp="label"
                placeholder="Select Client"
                showSearch
                style={{ width: 200 }}
              >
                <Option value="">All Clients</Option>
                {clients.map(client => (
                  <Option
                    key={client.id}
                    label={client.name}
                    value={String(client.id)}
                  >
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Assignee" name="engineerName">
              <Select
                allowClear
                placeholder="Select Assignee"
                style={{ width: 200 }}
              >
                <Option value="">All Assignees</Option>
                {engineers.map(eng => (
                  <Option key={eng} value={eng}>
                    {eng}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Date Range" name="dateRange">
              <RangePicker />
            </Form.Item>
            <Form.Item>
              <Button
                onClick={() => {
                  const resetFilters = {
                    status: 'All',
                    clientId: '',
                    engineerName: '',
                    dateRange: null,
                  };
                  // Reset form values
                  form.setFieldsValue(resetFilters);
                  // Update filters state
                  setFilters(prev => ({
                    ...prev,
                    ...resetFilters,
                  }));
                  // Reset pagination
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
              >
                Reset
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                onClick={() => {
                  const myTicketsFilters = {
                    ...filters,
                    engineerName: user?.displayName || '',
                  };
                  form.setFieldsValue(myTicketsFilters);
                  setFilters(myTicketsFilters);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
              >
                My Tickets
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns}
            dataSource={tickets}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                fetchTickets(true, page, pageSize);
              },
            }}
            rowKey="id"
          />
        </Card>

        <Modal
          footer={[
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Close
            </Button>,
            selectedTicket?.status === 'Acknowledged' && (
              <Button
                danger
                key="resolve"
                onClick={() =>
                  handleResolve(
                    selectedTicket!.incident_number,
                    selectedTicket!.source_id,
                  )
                }
                type="primary"
              >
                Mark as Resolved
              </Button>
            ),
            selectedTicket?.status === 'Resolved' && (
              <Button
                key="reopen"
                onClick={() => handleReopen(selectedTicket!.incident_number)}
                type="primary"
              >
                Reopen Ticket
              </Button>
            ),
          ]}
          onCancel={() => setIsModalVisible(false)}
          open={isModalVisible}
          title={
            selectedTicket
              ? `Ticket Details - #${selectedTicket.incident_number}`
              : 'Ticket Details'
          }
          width={800}
        >
          {selectedTicket && (
            <div
              style={{ maxHeight: '600px', overflowY: 'auto', padding: '4px' }}
            >
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <Typography.Text strong style={{ fontSize: '13px' }}>
                    Issue Summary:
                  </Typography.Text>
                  <div
                    style={{
                      background: '#f9f9f9',
                      padding: '8px',
                      borderRadius: '4px',
                      marginTop: '4px',
                      fontSize: '13px',
                    }}
                  >
                    {selectedTicket.description}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px 12px',
                    marginBottom: '8px',
                    fontSize: '13px',
                  }}
                >
                  <div>
                    <Typography.Text strong style={{ fontSize: '12px' }}>
                      Client:
                    </Typography.Text>{' '}
                    {selectedTicket.agent
                      ? `${selectedTicket.agent.first_name} ${selectedTicket.agent.last_name}`
                      : 'Unknown'}
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: '12px' }}>
                      Assignee:
                    </Typography.Text>{' '}
                    {selectedTicket.assignee || 'Unassigned'}
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: '12px' }}>
                      Created:
                    </Typography.Text>{' '}
                    {formatDateFromUTCtoAgent(
                      selectedTicket.created_at!,
                      selectedTicket.agent?.timezone || 'America/New_York',
                    )}
                  </div>
                  <div>
                    <Typography.Text strong style={{ fontSize: '12px' }}>
                      Status:
                    </Typography.Text>{' '}
                    <Tag
                      color={getStatusColor(selectedTicket.status)}
                      style={{ fontSize: '11px' }}
                    >
                      {selectedTicket.status}
                    </Tag>
                    {selectedTicket.status?.toLowerCase() === 'triggered' && (
                      <Button
                        icon={<CheckCircleOutlined />}
                        onClick={() =>
                          handleAcknowledge(
                            selectedTicket.incident_number,
                            selectedTicket.source_id,
                          )
                        }
                        size="small"
                        style={{ marginLeft: '8px' }}
                        type="primary"
                      >
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </div>

                {selectedTicket.conversation_thread &&
                  selectedTicket.conversation_thread.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <Typography.Title
                        level={5}
                        style={{ fontSize: '14px', marginBottom: '8px' }}
                      >
                        Conversation (
                        {selectedTicket.conversation_thread.length})
                      </Typography.Title>
                      <div
                        style={{
                          border: '1px solid #e8e8e8',
                          borderRadius: '6px',
                          padding: '8px',
                          background: '#fafafa',
                          maxHeight: '300px',
                          overflowY: 'auto',
                        }}
                      >
                        {selectedTicket.conversation_thread.map(note => (
                          <ConversationMessage
                            isAgent={
                              note.user?.summary?.includes(
                                selectedTicket.agent?.last_name || '',
                              ) || false
                            }
                            key={note.id}
                            message={{
                              content: note.content,
                              author: note.user?.summary || 'Unknown',
                              timestamp: note.created_at,
                            }}
                            timezone={selectedTicket.agent?.timezone}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                <div style={{ marginTop: '12px' }}>
                  <Collapse
                    defaultActiveKey={['intelligence']}
                    items={[
                      {
                        key: 'intelligence',
                        label: (
                          <span>
                            <BulbOutlined style={{ marginRight: 6 }} />
                            AI Intelligence
                          </span>
                        ),
                        children: intelligenceLoading ? (
                          <div
                            style={{
                              textAlign: 'center',
                              padding: '24px',
                            }}
                          >
                            <Spin size="small" />
                            <div
                              style={{
                                marginTop: 8,
                                color: '#999',
                                fontSize: 12,
                              }}
                            >
                              Analyzing similar incidents...
                            </div>
                          </div>
                        ) : intelligenceData ? (
                          <div style={{ fontSize: 13 }}>
                            <div style={{ marginBottom: 12 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: 8,
                                }}
                              >
                                <Tag
                                  color={
                                    intelligenceData.action_plan.confidence ===
                                    'high'
                                      ? 'green'
                                      : intelligenceData.action_plan
                                            .confidence === 'medium'
                                        ? 'orange'
                                        : 'red'
                                  }
                                >
                                  {intelligenceData.action_plan.confidence}{' '}
                                  confidence
                                </Tag>
                              </div>
                              <div style={{ lineHeight: 1.6, color: '#333' }}>
                                {intelligenceData.action_plan.summary}
                              </div>
                            </div>

                            <Collapse
                              ghost
                              items={[
                                ...(intelligenceData.action_plan.action_steps
                                  .length > 0
                                  ? [
                                      {
                                        key: 'steps',
                                        label: `Detailed Steps (${intelligenceData.action_plan.action_steps.length})`,
                                        children: (
                                          <ol
                                            style={{
                                              margin: '4px 0 0 0',
                                              paddingLeft: 20,
                                            }}
                                          >
                                            {intelligenceData.action_plan.action_steps.map(
                                              step => (
                                                <li
                                                  key={step.step_number}
                                                  style={{ marginBottom: 6 }}
                                                >
                                                  <strong>{step.action}</strong>
                                                  <div
                                                    style={{
                                                      color: '#666',
                                                      fontSize: 12,
                                                    }}
                                                  >
                                                    {step.reasoning}
                                                  </div>
                                                </li>
                                              ),
                                            )}
                                          </ol>
                                        ),
                                      },
                                    ]
                                  : []),
                                ...(intelligenceData.similar_incidents.length >
                                0
                                  ? [
                                      {
                                        key: 'similar',
                                        label: `Similar Incidents (${intelligenceData.similar_incidents.length})`,
                                        children: (
                                          <div>
                                            {intelligenceData.similar_incidents.map(
                                              inc => (
                                                <div
                                                  key={inc.incident_number}
                                                  style={{
                                                    padding: '6px 8px',
                                                    background: '#fafafa',
                                                    borderRadius: 4,
                                                    marginBottom: 6,
                                                    borderLeft:
                                                      '3px solid #1890ff',
                                                    fontSize: 12,
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      justifyContent:
                                                        'space-between',
                                                    }}
                                                  >
                                                    <strong>
                                                      #{inc.incident_number}
                                                    </strong>
                                                    <Tag>
                                                      {(
                                                        inc.score * 100
                                                      ).toFixed(0)}
                                                      % match
                                                    </Tag>
                                                  </div>
                                                  <div
                                                    style={{
                                                      color: '#333',
                                                      marginTop: 2,
                                                    }}
                                                  >
                                                    {inc.description}
                                                  </div>
                                                  {inc.resolution_notes && (
                                                    <div
                                                      style={{
                                                        color: '#52c41a',
                                                        marginTop: 2,
                                                      }}
                                                    >
                                                      Resolution:{' '}
                                                      {inc.resolution_notes}
                                                    </div>
                                                  )}
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        ),
                                      },
                                    ]
                                  : []),
                                ...(intelligenceData.action_plan
                                  .patterns_observed.length > 0
                                  ? [
                                      {
                                        key: 'patterns',
                                        label: `Patterns (${intelligenceData.action_plan.patterns_observed.length})`,
                                        children: (
                                          <ul
                                            style={{
                                              margin: '4px 0 0 0',
                                              paddingLeft: 20,
                                              fontSize: 12,
                                              color: '#666',
                                            }}
                                          >
                                            {intelligenceData.action_plan.patterns_observed.map(
                                              (pattern, i) => (
                                                <li key={i}>{pattern}</li>
                                              ),
                                            )}
                                          </ul>
                                        ),
                                      },
                                    ]
                                  : []),
                              ]}
                              size="small"
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              color: '#999',
                              fontSize: 12,
                              textAlign: 'center',
                              padding: 12,
                            }}
                          >
                            No intelligence data available
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>

                {selectedTicket.resolution && (
                  <div style={{ marginTop: '12px' }}>
                    <Typography.Text strong style={{ fontSize: '13px' }}>
                      Resolution:
                    </Typography.Text>
                    <div
                      style={{
                        background: '#f6ffed',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #b7eb8f',
                        marginTop: '4px',
                        fontSize: '13px',
                      }}
                    >
                      {selectedTicket.resolution}
                      {selectedTicket.resolution_notes && (
                        <div
                          style={{
                            marginTop: '4px',
                            fontStyle: 'italic',
                            fontSize: '12px',
                          }}
                        >
                          Note: {selectedTicket.resolution_notes}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: '12px',
                    borderTop: '1px solid #e8e8e8',
                    paddingTop: '12px',
                  }}
                >
                  <Input.TextArea
                    onChange={e => setReplyContent(e.target.value)}
                    placeholder="Type your note here..."
                    rows={3}
                    style={{ fontSize: '13px' }}
                    value={replyContent}
                  />
                  <div style={{ marginTop: '8px', textAlign: 'right' }}>
                    <Button
                      loading={isSubmittingReply}
                      onClick={handleAddNote}
                      size="small"
                      type="primary"
                    >
                      Add Note
                    </Button>
                  </div>
                </div>
              </Space>
            </div>
          )}
        </Modal>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div style={{ textAlign: 'center', marginTop: '100px' }}>
          <Typography.Title level={3}>
            Please sign in to access Support Ticket Management
          </Typography.Title>
          <SignInButton loginType="popup" />
        </div>
      </UnauthenticatedTemplate>
    </div>
  );
}
